import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@mui/material';
import { SkeletonPathCategory } from '@/components/navigation/NavigationIntroScreen/components/SkeletonPathCategory';
import DeviceManager from '../util/deviceManager';
import { NavItemsSlider } from './search-bar/components/NavItemsSlider';
import { useCategoriesStyles } from './styles';
import { DefaultCategoryIcon } from './NavigationIntroScreen/components/Popup/Icons';
import { FAIcon } from '@packages/ui-components';

export const NavigationCategories = ({
  categories,
  handleSelectCategory,
  rtl,
  showSkeleton,
  mobilePosition,
}: {
  rtl?: boolean;
  categories: any[];
  handleSelectCategory: (category: { [key: string]: string | number }) => void;
  showSkeleton: boolean;
  mobilePosition: number;
}) => {
  const gridRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isCanScroll, setIsCanScroll] = useState(true);

  const filteredCategories = useMemo(
    () =>
      categories
        ?.filter((c) => !!c?.items?.length)
        .sort((a: { order: number }, b: { order: number }) => a.order - b.order),
    [categories],
  );
  const { categoriesGridWrapperStyle, gridItemInnerStyle, gridItemTitleStyle } =
    useCategoriesStyles();

  const handleScroll = () => {
    if (gridRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = gridRef.current;
      setIsCanScroll(scrollTop === 0 || Math.round(scrollTop + clientHeight) !== scrollHeight);
    }
    if (gridRef.current) {
      clearTimeout(gridRef.current.timeout);
    }
  };

  const scrollToBottom = () => {
    if (gridRef.current) {
      gridRef.current.scrollTo({
        top: gridRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  const renderIcon = ({
    icon,
    iconColor,
    title,
    tourId,
  }: {
    icon: string | null;
    iconColor: string | null;
    title: string;
    tourId: number;
  }) => {
    if (!icon) {
      return <DefaultCategoryIcon style={{ width: '20px', height: '20px', color: iconColor }} />;
    }
    if (icon.endsWith('.webp')) {
      const iconSrc = `${process.env.NEXT_PUBLIC_CDN}/tours/${tourId}/navigation/icons/${icon}`;
      return <img width="20px" height="20px" src={iconSrc} alt={title} />;
    }
    const iconSrc = `${
      process.env.NEXT_PUBLIC_API
    }/v1/public/icons/${icon}?hex=${encodeURIComponent(iconColor)}`;
    return <img width="20px" height="20px" src={iconSrc} alt={title} />;
  };

  useEffect(() => {
    const checkOverflow = () => {
      if (gridRef.current) {
        const { scrollHeight, clientHeight } = gridRef.current;
        setIsOverflowing(scrollHeight > clientHeight);
      }
    };

    if (categories?.length > 0) {
      setTimeout(() => {
        checkOverflow();
      }, 1000);
    }

    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [categories?.length]);

  return (
    <>
      {(DeviceManager.isDesktop() ||
        (DeviceManager.isMobile() && (filteredCategories.length <= 3 || mobilePosition > 1))) && (
        <Grid
          id="categories-grid-container"
          ref={gridRef}
          onScroll={handleScroll}
          container
          spacing="10px"
          justifyContent={{ lg: rtl ? 'end' : 'start' }}
          sx={{
            ...categoriesGridWrapperStyle,
          }}
        >
          {showSkeleton ? (
            <>
              <SkeletonPathCategory />
              <SkeletonPathCategory />
              <SkeletonPathCategory />
              <SkeletonPathCategory />
              <SkeletonPathCategory />
              <SkeletonPathCategory />
            </>
          ) : (
            filteredCategories.map((category) => {
              const hasItems = category.items?.length > 0;
              return (
                <Grid
                  item
                  // eslint-disable-next-line no-nested-ternary
                  xs={
                    DeviceManager.isMobile()
                      ? filteredCategories?.length === 1
                        ? 12
                        : filteredCategories?.length === 2
                        ? 6
                        : 4
                      : undefined
                  }
                  key={category.id}
                  onClick={() => handleSelectCategory(category)}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    minWidth: '154px',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '6px',
                    flex: '1 0 0',
                  }}
                >
                  <Stack
                    sx={{ ...gridItemInnerStyle, visibility: hasItems ? 'visibility' : 'hidden' }}
                  >
                    <Stack
                      width="30px"
                      height="30px"
                      sx={{
                        background: category.backgroundColor || '#2055FF',
                        borderRadius: '8px',
                        p: '6px 5px',
                      }}
                    >
                      {renderIcon(category)}
                    </Stack>
                    <Stack overflow="hidden">
                      <Typography
                        sx={{
                          ...gridItemTitleStyle,
                          direction: rtl ? 'rtl' : 'ltr',
                          color: '#000000',
                        }}
                      >
                        {category.title}
                      </Typography>
                    </Stack>
                  </Stack>
                </Grid>
              );
            })
          )}
          {isOverflowing && isCanScroll && !DeviceManager.isMobile() && (
            <Stack
              sx={{
                position: 'fixed',
                left: '50%',
                bottom: '12%',
                alignItems: 'center',
                justifyContent: 'center',
                transform: 'translateX(-50%)',
                zIndex: 20,
                pointerEvents: 'auto',
                display: {
                  md: 'flex',
                  xs: 'none',
                },
              }}
            >
              <Button
                onClick={scrollToBottom}
                endIcon={
                  <FAIcon icon="faArrowDown" size={12} />
                }
                sx={{
                  color: '#344054',
                  p: '8px 16px 8px 18px', // Slightly larger padding for better integration
                  borderRadius: '16px',
                  border: '1px solid #EAECF0',
                  background: '#F9FAFB',
                  fontSize: '14px',
                  fontWeight: 500,
                  textTransform: 'none',
                  boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
                  '&:hover': {
                    background: '#F2F4F7',
                    color: '#344054',
                    boxShadow: '0px 1px 3px rgba(16, 24, 40, 0.1)',
                  },
                }}
              >
                View more
              </Button>
            </Stack>
          )}
        </Grid>
      )}
      {DeviceManager.isMobile() && filteredCategories.length > 3 && mobilePosition === 1 && (
        <NavItemsSlider
          categories={filteredCategories}
          showSkeleton={showSkeleton}
          handleItemSelect={handleSelectCategory}
          rtl={rtl}
          renderCategoryIcon={renderIcon}
        />
      )}
    </>
  );
};
